<template>
  <!-- Hero Section -->
  <section class="py-12 md:py-20">
    <div class="max-w-7xl mx-auto px-4">
      <div class="flex flex-col-reverse md:flex-row gap-8 md:gap-12 items-center">
        <!-- Left content -->
        <div class="md:w-5/12 flex flex-col">
          <h1 class="text-4xl md:text-5xl font-bold tracking-tight text-center md:text-left">
            Unidade Pastoral<br>de Chelas
          </h1>
          <p class="mt-6 text-base text-muted-foreground text-justify md:text-left">
            A unidade Pastoral de Chelas é constituída pela Paróquia São Maximiliano Kolbe do Vale de
            Chelas e pela Paróquia de Santa Beatriz da Silva. A paróquia de São Maximiliano Kolbe do Vale de Chelas,
            além da Igreja matriz (s. Maximiliano K.) tem no seu território a Igreja de Santa Clara de Assis.
            Todas encontram-se na freguesia de Marvila e fazem parte da Vigararia "Lisboa II". Navegue no site para
            saber mais sobre as suas igrejas,
            sacramentos, vida cristã, contactos e muito mais.
            Como em todas as paróquias, existem dois Conselhos que ajudam o pároco e os freis na orientação pastoral e
            económica da realidade eclesial: o Conselho Pastoral Paroquial (CPP) e o Conselho para os Assuntos
            Económicos (CPAE). Enquanto os CPAE devem permanecer distintos (um Conselho para cada paróquia) os CPP, de
            vez em quando, convocados pelo pároco reúnem-se num encontro unitário.
          </p>
          <div class="flex flex-wrap gap-3 mt-8 justify-center md:justify-start">
            <router-link
              :to="{ name: 'baptism' }"
              class="inline-flex items-center justify-center h-9 px-4 bg-primary text-primary-foreground font-medium text-sm rounded-md hover:bg-primary/90 transition-colors duration-150"
            >
              Batismo
            </router-link>
            <router-link
              :to="{ name: 'marriage' }"
              class="inline-flex items-center justify-center h-9 px-4 bg-primary text-primary-foreground font-medium text-sm rounded-md hover:bg-primary/90 transition-colors duration-150"
            >
              Matrimónio
            </router-link>
            <router-link
              :to="{ name: 'catechism' }"
              class="inline-flex items-center justify-center h-9 px-4 bg-primary text-primary-foreground font-medium text-sm rounded-md hover:bg-primary/90 transition-colors duration-150"
            >
              Catequese
            </router-link>
          </div>
        </div>

        <!-- Hero carousel -->
        <div class="md:w-6/12 w-full">
          <UCarousel
            :slides="heroImages"
            :interval="3000"
            container-class="h-64 md:h-[400px] rounded-lg overflow-hidden"
          >
            <template #default="{ slide }">
              <img
                :src="slide"
                class="w-full h-full object-cover"
                alt="Imagem da igreja"
              />
            </template>
          </UCarousel>
        </div>
      </div>
    </div>
  </section>

  <!-- Churches Section -->
  <section class="py-12 md:py-20 bg-accent">
    <div class="max-w-7xl mx-auto px-4">
      <div class="text-center mb-10">
        <h2 class="text-2xl md:text-3xl font-semibold tracking-tight">Igrejas da Unidade Pastoral</h2>
        <p class="mt-2 text-muted-foreground">Conheça os horários das missas em cada igreja</p>
      </div>

      <div class="grid md:grid-cols-3 gap-6">
        <!-- Santa Beatriz -->
        <div class="group rounded-lg border border-border bg-card overflow-hidden transition-colors duration-150 hover:bg-accent/50">
          <div class="overflow-hidden">
            <img
              src="@/assets/images/sta_beatriz/igreja.jpg"
              alt="Igreja de Santa Beatriz da Silva"
              class="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
          <div class="p-6">
            <h4 class="text-lg font-semibold tracking-tight mb-4">Sta. Beatriz da Silva</h4>
            <dl class="space-y-3 text-sm">
              <div>
                <dt class="font-medium text-foreground">Missas Semanais</dt>
                <dd class="text-muted-foreground">3ª a 6ª Feira às 18h</dd>
              </div>
              <div>
                <dt class="font-medium text-foreground">Missas Dominicais</dt>
                <dd class="text-muted-foreground">Sábado às 17h30</dd>
                <dd class="text-muted-foreground">Domingo às 10h</dd>
              </div>
            </dl>
          </div>
        </div>

        <!-- Santa Clara -->
        <div class="group rounded-lg border border-border bg-card overflow-hidden transition-colors duration-150 hover:bg-accent/50">
          <div class="overflow-hidden">
            <img
              src="@/assets/images/sta_clara/igreja.jpg"
              alt="Igreja de Santa Clara"
              class="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
          <div class="p-6">
            <h4 class="text-lg font-semibold tracking-tight mb-4">Sta. Clara de Assis</h4>
            <dl class="space-y-3 text-sm">
              <div>
                <dt class="font-medium text-foreground">Missas Semanais</dt>
                <dd class="text-muted-foreground">3ª a 6ª Feira às 19h</dd>
              </div>
              <div>
                <dt class="font-medium text-foreground">Missas Dominicais</dt>
                <dd class="text-muted-foreground">Sábado às 19h</dd>
                <dd class="text-muted-foreground">Domingo às 9h e às 10h30</dd>
              </div>
            </dl>
          </div>
        </div>

        <!-- São Maximiliano -->
        <div class="group rounded-lg border border-border bg-card overflow-hidden transition-colors duration-150 hover:bg-accent/50">
          <div class="overflow-hidden">
            <img
              src="@/assets/images/sao_max/igreja.jpg"
              alt="Igreja de São Maximiliano de Kolbe"
              class="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
          <div class="p-6">
            <h4 class="text-lg font-semibold tracking-tight mb-4">S. Maximiliano Kolbe</h4>
            <dl class="space-y-3 text-sm">
              <div>
                <dt class="font-medium text-foreground">Missas Semanais</dt>
                <dd class="text-muted-foreground">3ª a 6ª Feira às 18h30</dd>
              </div>
              <div>
                <dt class="font-medium text-foreground">Missas Dominicais</dt>
                <dd class="text-muted-foreground">Sábado às 18h30</dd>
                <dd class="text-muted-foreground">Domingo às 9h e às 11h</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- News and Calendar Section -->
  <section v-if="news || calendar" class="py-12 md:py-20">
    <div class="max-w-7xl mx-auto px-4">
      <div class="grid gap-8" :class="news && calendar ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-2xl mx-auto'">
        <!-- News -->
        <div v-if="news">
          <h4 class="text-xl font-semibold tracking-tight mb-6 text-center">Avisos</h4>
          <div class="rounded-lg border border-border overflow-hidden">
            <div
              v-for="(item, index) in news"
              :key="item.bulletin_id"
              :class="[
                'flex items-center justify-between px-4 py-3 transition-colors duration-150 hover:bg-accent/50',
                index !== news.length - 1 ? 'border-b border-border' : ''
              ]"
            >
              <span class="font-medium text-sm">{{ item.title }}</span>
              <button
                @click="openNews(item)"
                class="inline-flex items-center justify-center h-8 px-3 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors duration-150"
              >
                Abrir
              </button>
            </div>
          </div>
        </div>

        <!-- Calendar -->
        <div v-if="calendar">
          <h4 class="text-xl font-semibold tracking-tight mb-6">Calendário</h4>
          <div class="rounded-lg border border-border overflow-hidden">
            <div
              v-for="(val, key, index) in calendar"
              :key="key"
              :class="[
                'flex gap-4 px-4 py-3 transition-colors duration-150 hover:bg-accent/50',
                index !== Object.keys(calendar).length - 1 ? 'border-b border-border' : ''
              ]"
            >
              <div class="flex-shrink-0 w-12 text-center">
                <span class="block text-lg font-semibold">{{ key.split(' ')[0] }}</span>
                <span class="block text-xs uppercase text-muted-foreground">{{ key.split(' ')[1] }}</span>
              </div>
              <div class="flex-1 space-y-1">
                <div
                  v-for="item in val"
                  :key="item.timetable_id"
                  class="text-sm"
                >
                  <span class="font-medium">{{ item.time }}</span>
                  <span class="text-muted-foreground ml-1">{{ item.type_name }} {{ item.notes }} {{ item.place }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Pastoral Year Section -->
  <section class="py-12 md:py-20 bg-accent">
    <div class="max-w-7xl mx-auto px-4">
      <div class="text-center mb-10">
        <h2 class="text-xl md:text-2xl font-semibold tracking-tight">"Se pedirdes alguma coisa em meu nome, Eu o farei" (Jo 14,14)</h2>
        <p class="text-sm text-muted-foreground mt-2">(tema do ano pastoral 2023-24)</p>
      </div>

      <div class="max-w-3xl mx-auto text-sm text-muted-foreground space-y-4">
        <p>
          Além deste guia bíblico, o ano pastoral é marcado por numerosos eventos, diocesanos e franciscanos. A 16 de Setembro começou o Ano Vicentino, lembrando os 850 anos da chegada das relíquias do santo diácono Vicente, mártir. Até 16 de setembro 2024 haverá iniciativas litúrgicas e culturais para conhecer melhor este santo, padroeiro da Diocese e da cidade. Papa Francisco proclamou um ano de Indulgência plenária.
          A família franciscana, de 2023 a 2026, tem cinco centenários para celebrar:
        </p>
        <ul class="list-disc pl-6 space-y-1">
          <li>800 anos da aprovação da Regra de s. Francisco (29 de Novembro 2023)</li>
          <li>800 anos do Presépio de s. Francisco a Greccio (25 de Dezembro 2023)</li>
          <li>800 anos da impressão dos Estígmas de s. Francisco (17 de Setembro de 2024)</li>
          <li>800 anos da composição do Cântico das Criaturas (Primavera 2025)</li>
          <li>800 anos da morte de s. Francisco de Assis (4 de Outubro 2026)</li>
        </ul>
        <p>São todas ocasiões para louvar juntos o Senhor e seguir mais de perto os seus passos.</p>
      </div>
    </div>
  </section>

  <!-- Gallery Section -->
  <section class="py-12 md:py-20">
    <div class="max-w-7xl mx-auto px-4">
      <div class="grid md:grid-cols-12 gap-6">
        <!-- Info -->
        <div class="md:col-span-3">
          <div class="rounded-lg bg-secondary/20 p-6 h-full flex flex-col">
            <h4 class="text-lg font-semibold tracking-tight mb-4">Galeria</h4>
            <p class="text-sm text-muted-foreground flex-1">
              Nesta galeria encontra algumas fotos da nossa Unidade Pastoral. As fotos presentes
              mostram momentos litúrgicos, atividades associadas à Jornadas Mundias da Juventude e algumas atividades
              com idosos, jovens, crianças da nossa comunidade.
            </p>
            <button
              @click="$refs.gallery.start()"
              class="w-full h-10 mt-6 inline-flex items-center justify-center bg-primary text-primary-foreground font-medium text-sm rounded-md hover:bg-primary/90 transition-colors duration-150"
            >
              Ver Galeria
            </button>
          </div>
        </div>

        <!-- Main image -->
        <div class="md:col-span-6">
          <div
            class="group relative overflow-hidden rounded-lg cursor-pointer h-full"
            @click="$refs.gallery.start()"
          >
            <img
              src="@/assets/images/gallery/0.jpg"
              alt="Galeria"
              class="w-full h-64 md:h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
          </div>
        </div>

        <!-- Side image -->
        <div class="md:col-span-3">
          <div
            class="group relative overflow-hidden rounded-lg cursor-pointer h-full"
            @click="$refs.gallery.start()"
          >
            <img
              src="@/assets/images/gallery/15.jpg"
              alt="Galeria"
              class="w-full h-64 md:h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Gallery component -->
  <Gallery ref="gallery" />

  <!-- News Modal -->
  <UModal v-model="newsModalOpen" :title="selectedNew?.title" size="lg">
    <div v-if="selectedNew" v-html="selectedNew.description" class="prose prose-sm max-w-none" />
    <template #footer>
      <a
        v-if="selectedNew?.pdf_url || selectedNew?.attachment_url"
        :href="selectedNew.pdf_url || selectedNew.attachment_url"
        target="_blank"
        class="inline-flex items-center justify-center h-9 px-4 bg-primary text-primary-foreground font-medium text-sm rounded-md hover:bg-primary/90 transition-colors duration-150"
      >
        Ver Anexo
      </a>
      <button
        @click="newsModalOpen = false"
        class="inline-flex items-center justify-center h-9 px-4 border border-border bg-background font-medium text-sm rounded-md hover:bg-accent transition-colors duration-150"
      >
        Fechar
      </button>
    </template>
  </UModal>

  <!-- Announcement Modal -->
  <UModal
    v-model="announcementModalOpen"
    :show-close="true"
    :close-on-backdrop="true"
    modal-class="!p-0 !overflow-hidden"
    body-class="!p-0"
  >
    <template #header>
      <span></span>
    </template>
    <img
      v-if="announcement"
      :src="announcement.imageUrl"
      alt="Anúncio"
      class="w-full h-auto max-h-[80vh] object-contain"
    />
  </UModal>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Gallery from "@/components/Gallery.vue"
import UCarousel from "@/components/ui/UCarousel.vue"
import UModal from "@/components/ui/UModal.vue"

const news = ref(null)
const calendar = ref(null)
const selectedNew = ref(null)
const announcement = ref(null)
const newsModalOpen = ref(false)
const announcementModalOpen = ref(false)

const heroImages = [
  require("@/assets/images/gallery/23.jpg"),
  require("@/assets/images/gallery/2.jpg"),
  require("@/assets/images/gallery/34.jpg"),
]

const openNews = (item) => {
  selectedNew.value = item
  newsModalOpen.value = true
}

onMounted(() => {
  // Fetch announcement config
  fetch("/config.json")
    .then(async (res) => {
      const config = await res.json()
      if (config.announcement && config.announcement.enabled) {
        const displayFrom = new Date(config.announcement.displayFrom)
        const displayUntil = new Date(config.announcement.displayUntil)
        const now = new Date()

        if (now >= displayFrom && now <= displayUntil) {
          announcement.value = config.announcement
          setTimeout(() => {
            announcementModalOpen.value = true
          }, 1000)
        }
      }
    })
    .catch(() => {})

  // Fetch news
  fetch("https://app.upchelas.com/api/bulletins/list", {
    method: "POST",
  }).then(async (res) => {
    if (!res.ok) return
    const data = await res.json()
    if (!data.data || !data.data.bulletins) return
    news.value = data.data.bulletins.filter((obj) => obj.type !== 0).slice(0, 10).map((obj) => {
      return {
        ...obj,
        description: obj.description.replaceAll('\n', '<br><br>')
      }
    })
  }).catch(() => {})

  // Fetch calendar
  fetch("https://app.upchelas.com/api/timetables/list", {
    method: "POST",
  }).then(async (res) => {
    if (!res.ok) return
    const data = await res.json()
    if (!data.data || !data.data.timetables) return

    const days = {}
    data.data.timetables.forEach((obj) => {
      const ts = new Date(obj.date * 1000)
      const dia = ts.toLocaleDateString('pt-PT', { day: '2-digit' })
      const mes = ts.toLocaleDateString('pt-PT', { month: 'short' }).replace('.', '')
      const date = `${dia} ${mes}`
      const time = ts.toISOString().slice(11, 16)

      if (!days[date]) {
        days[date] = []
      }

      days[date].push({
        ...obj,
        ts: ts,
        date: date,
        time: time,
      })
    })

    calendar.value = days
  }).catch(() => {})
})
</script>
